import { createSlice } from "@reduxjs/toolkit"
import { withFirestoreHydration } from "../middleware/firestorePersistence"

// Initial state for categories
const initialState = {
  categories: {},
  categoryGroups: {},
  defaultCategories: {},
  categoryRules: {},
  isLoading: false,
  error: null,
  _hydrated: false,
}

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    // Category management
    setCategories: (state, action) => {
      state.categories = action.payload
      state.isLoading = false
    },

    addCategory: (state, action) => {
      const category = action.payload
      state.categories[category.id] = {
        ...category,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    updateCategory: (state, action) => {
      const { categoryId, updates } = action.payload
      if (state.categories[categoryId]) {
        state.categories[categoryId] = {
          ...state.categories[categoryId],
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    deleteCategory: (state, action) => {
      const categoryId = action.payload
      delete state.categories[categoryId]
    },

    // Category groups management
    setCategoryGroups: (state, action) => {
      state.categoryGroups = action.payload
    },

    addCategoryGroup: (state, action) => {
      const group = action.payload
      state.categoryGroups[group.id] = {
        ...group,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    updateCategoryGroup: (state, action) => {
      const { groupId, updates } = action.payload
      if (state.categoryGroups[groupId]) {
        state.categoryGroups[groupId] = {
          ...state.categoryGroups[groupId],
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    deleteCategoryGroup: (state, action) => {
      const groupId = action.payload
      delete state.categoryGroups[groupId]
    },

    // Default categories
    setDefaultCategories: (state, action) => {
      state.defaultCategories = action.payload
    },

    // Category rules for auto-categorization
    setCategoryRules: (state, action) => {
      state.categoryRules = action.payload
    },

    addCategoryRule: (state, action) => {
      const rule = action.payload
      state.categoryRules[rule.id] = {
        ...rule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    },

    updateCategoryRule: (state, action) => {
      const { ruleId, updates } = action.payload
      if (state.categoryRules[ruleId]) {
        state.categoryRules[ruleId] = {
          ...state.categoryRules[ruleId],
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    deleteCategoryRule: (state, action) => {
      const ruleId = action.payload
      delete state.categoryRules[ruleId]
    },

    // Bulk operations
    bulkAddCategories: (state, action) => {
      const categories = action.payload
      categories.forEach((category) => {
        state.categories[category.id] = {
          ...category,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      })
    },

    bulkUpdateCategories: (state, action) => {
      const updates = action.payload
      Object.keys(updates).forEach((categoryId) => {
        if (state.categories[categoryId]) {
          state.categories[categoryId] = {
            ...state.categories[categoryId],
            ...updates[categoryId],
            updatedAt: new Date().toISOString(),
          }
        }
      })
    },

    bulkDeleteCategories: (state, action) => {
      const categoryIds = action.payload
      categoryIds.forEach((categoryId) => {
        delete state.categories[categoryId]
      })
    },

    // Category assignment
    assignCategoryToGroup: (state, action) => {
      const { categoryId, groupId } = action.payload
      if (state.categories[categoryId]) {
        state.categories[categoryId] = {
          ...state.categories[categoryId],
          groupId,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    removeCategoryFromGroup: (state, action) => {
      const categoryId = action.payload
      if (state.categories[categoryId]) {
        const { groupId, ...categoryWithoutGroup } =
          state.categories[categoryId]
        state.categories[categoryId] = {
          ...categoryWithoutGroup,
          updatedAt: new Date().toISOString(),
        }
      }
    },

    // Category ordering
    reorderCategories: (state, action) => {
      const { categoryIds } = action.payload
      categoryIds.forEach((categoryId, index) => {
        if (state.categories[categoryId]) {
          state.categories[categoryId] = {
            ...state.categories[categoryId],
            order: index,
            updatedAt: new Date().toISOString(),
          }
        }
      })
    },

    reorderCategoryGroups: (state, action) => {
      const { groupIds } = action.payload
      groupIds.forEach((groupId, index) => {
        if (state.categoryGroups[groupId]) {
          state.categoryGroups[groupId] = {
            ...state.categoryGroups[groupId],
            order: index,
            updatedAt: new Date().toISOString(),
          }
        }
      })
    },

    // UI state
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
      state.isLoading = false
    },

    clearError: (state) => {
      state.error = null
    },

    // Firestore hydration (handled by middleware)
    hydrateFromFirestore: (state, action) => {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    },
  },
})

// Export actions
export const {
  setCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  setCategoryGroups,
  addCategoryGroup,
  updateCategoryGroup,
  deleteCategoryGroup,
  setDefaultCategories,
  setCategoryRules,
  addCategoryRule,
  updateCategoryRule,
  deleteCategoryRule,
  bulkAddCategories,
  bulkUpdateCategories,
  bulkDeleteCategories,
  assignCategoryToGroup,
  removeCategoryFromGroup,
  reorderCategories,
  reorderCategoryGroups,
  setLoading,
  setError,
  clearError,
  hydrateFromFirestore,
} = categoriesSlice.actions

// Selectors
export const selectCategories = (state) => state.categories.categories
export const selectCategoryById = (state, categoryId) =>
  state.categories.categories[categoryId]
export const selectCategoryGroups = (state) => state.categories.categoryGroups
export const selectCategoryGroupById = (state, groupId) =>
  state.categories.categoryGroups[groupId]
export const selectDefaultCategories = (state) =>
  state.categories.defaultCategories
export const selectCategoryRules = (state) => state.categories.categoryRules
export const selectCategoriesLoading = (state) => state.categories.isLoading
export const selectCategoriesError = (state) => state.categories.error
export const selectCategoriesHydrated = (state) => state.categories._hydrated

// Composite selectors
export const selectCategoriesByGroup = (state) => {
  const categories = state.categories.categories
  const groups = state.categories.categoryGroups

  const grouped = {}

  // Add ungrouped categories
  grouped.ungrouped = {
    id: "ungrouped",
    name: "Ungrouped",
    categories: [],
  }

  // Initialize groups
  Object.values(groups).forEach((group) => {
    grouped[group.id] = {
      ...group,
      categories: [],
    }
  })

  // Assign categories to groups
  Object.values(categories).forEach((category) => {
    const groupId = category.groupId || "ungrouped"
    if (grouped[groupId]) {
      grouped[groupId].categories.push(category)
    }
  })

  // Sort categories within each group by order
  Object.values(grouped).forEach((group) => {
    group.categories.sort((a, b) => (a.order || 0) - (b.order || 0))
  })

  return grouped
}

export const selectOrderedCategories = (state) => {
  const categories = Object.values(state.categories.categories)
  return categories.sort((a, b) => (a.order || 0) - (b.order || 0))
}

export const selectOrderedCategoryGroups = (state) => {
  const groups = Object.values(state.categories.categoryGroups)
  return groups.sort((a, b) => (a.order || 0) - (b.order || 0))
}

export const selectActiveCategoryRules = (state) => {
  const rules = Object.values(state.categories.categoryRules)
  return rules.filter((rule) => rule.isActive !== false)
}

export const selectCategoriesForAutocomplete = (state) => {
  const categories = Object.values(state.categories.categories)
  return categories
    .filter((category) => category.isActive !== false)
    .map((category) => ({
      id: category.id,
      name: category.name,
      color: category.color,
      icon: category.icon,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// Enhanced reducer with Firestore hydration
export default withFirestoreHydration(categoriesSlice.reducer)
